import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MailWarning } from 'lucide-react';

import { authService } from '../services/api';
import { Button } from '@/components/ui/button';

/** Banner persistente para cuentas sin verificar (auth 007 FR-014).
    "Entrar sí, operar no": informa y ofrece reenvío; no bloquea la navegación. */
function VerifyEmailBanner() {
  const location = useLocation();
  const [user, setUser] = useState<{ email: string; email_verified: boolean } | null>(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const timer = useRef<number>();

  // El timeout de "Correo reenviado ✓" se limpia SOLO al desmontar — si se
  // limpiara en cada cambio de ruta, sent quedaría en true indefinidamente
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Revalida en cada cambio de ruta: si el usuario verifica y navega dentro
  // del SPA, el banner desaparece sin recargar la página
  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem('access_token')) {
      // logout: el banner desaparece de inmediato, sin datos viejos
      setUser(null);
      return;
    }
    authService
      .getMe()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        // token expirado/inválido: sin sesión válida no hay banner
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!user || user.email_verified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await authService.resendVerification();
      // confirmación temporal: el botón vuelve (el backend permite 3/hora)
      setSent(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setSent(false), 45_000);
    } catch {
      // rate limit u error transitorio: el estado "enviado" no se finge
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="status"
      className="mx-4 mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-400/60 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <MailWarning className="h-5 w-5 shrink-0 text-amber-500" />
      <span className="min-w-[220px] flex-1">
        Te enviamos un correo a <strong>{user.email}</strong>. Verifica tu cuenta para poder
        publicar propiedades y gestionar rentas — también puedes teclear tu código en{' '}
        <Link to="/verificar" className="font-medium underline">
          /verificar
        </Link>
        .
      </span>
      {sent ? (
        <span className="font-medium text-emerald-700">Correo reenviado ✓</span>
      ) : (
        <Button variant="outline" size="sm" onClick={resend} disabled={sending}>
          {sending ? 'Enviando…' : 'Reenviar correo'}
        </Button>
      )}
    </div>
  );
}

export default VerifyEmailBanner;
